import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  Package, RefreshCw, AlertTriangle, ShoppingCart, Truck, CheckCircle,
  Settings, TrendingUp, Clock, DollarSign, BarChart3, Plus
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AutoReorderDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [showReorderDialog, setShowReorderDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [reorderQty, setReorderQty] = useState(0);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/phase61/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setDashboardData(await response.json());
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const triggerReorder = async () => {
    if (!selectedItem) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/phase61/trigger-reorder/${selectedItem.id}${reorderQty ? `?quantity=${reorderQty}` : ''}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Reorder triggered successfully');
        setShowReorderDialog(false);
        fetchDashboard();
      }
    } catch (error) {
      toast.error('Failed to trigger reorder');
    }
  };

  const StatCard = ({ title, value, subtitle, icon: Icon, color = 'blue' }) => (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-600 font-medium">{title}</p>
            <p className="text-2xl font-bold text-slate-900 mt-1">{value}</p>
            {subtitle && <p className="text-xs text-slate-500 mt-1 font-medium">{subtitle}</p>}
          </div>
          <div className={`p-3 rounded-full bg-${color}-100`}>
            <Icon className={`h-6 w-6 text-${color}-600`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <RefreshCw className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="auto-reorder-dashboard">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Auto-Reorder System</h1>
              <p className="text-sm text-slate-600 mt-1 font-medium">Automated inventory management and reordering</p>
            </div>
            <Button variant="outline" onClick={fetchDashboard}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="dashboard" className="font-semibold">Dashboard</TabsTrigger>
            <TabsTrigger value="inventory" className="font-semibold">Inventory</TabsTrigger>
            <TabsTrigger value="orders" className="font-semibold">Orders</TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard" className="space-y-6">
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <StatCard title="Total Items" value={dashboardData?.stats?.total_items || 0} icon={Package} color="blue" />
              <StatCard title="Low Stock" value={dashboardData?.stats?.low_stock_items || 0} icon={AlertTriangle} color="orange" />
              <StatCard title="Pending Orders" value={dashboardData?.stats?.pending_orders || 0} icon={ShoppingCart} color="purple" />
              <StatCard title="Orders This Month" value={dashboardData?.stats?.orders_this_month || 0} icon={Truck} color="green" />
              <StatCard title="Monthly Savings" value={`$${dashboardData?.stats?.savings_this_month || 0}`} icon={DollarSign} color="teal" />
            </div>

            {/* Low Stock Alerts */}
            <Card>
              <CardHeader>
                <CardTitle className="text-slate-900 font-bold flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-orange-500" />
                  Low Stock Items
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {dashboardData?.inventory?.filter(i => i.is_low).map((item) => (
                    <div key={item.id} className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold text-slate-900">{item.name}</span>
                        <Badge variant="outline" className="text-orange-600 border-orange-300">{item.category}</Badge>
                      </div>
                      <div className="text-sm text-slate-600 mb-3">
                        <p>Current: <span className="font-bold text-red-600">{item.current_stock}</span> / Min: {item.min_stock}</p>
                      </div>
                      <Button size="sm" className="w-full bg-orange-500 hover:bg-orange-600" onClick={() => {
                        setSelectedItem(item);
                        setReorderQty(item.reorder_qty);
                        setShowReorderDialog(true);
                      }}>
                        <ShoppingCart className="h-4 w-4 mr-2" />
                        Reorder Now
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Recent Orders */}
            <Card>
              <CardHeader>
                <CardTitle className="text-slate-900 font-bold">Recent Auto-Orders</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="px-4 py-3 text-sm font-bold text-slate-900">Order #</th>
                        <th className="px-4 py-3 text-sm font-bold text-slate-900">Item</th>
                        <th className="px-4 py-3 text-sm font-bold text-slate-900">Qty</th>
                        <th className="px-4 py-3 text-sm font-bold text-slate-900">Status</th>
                        <th className="px-4 py-3 text-sm font-bold text-slate-900">Expected</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dashboardData?.recent_orders?.slice(0, 5).map((order) => (
                        <tr key={order.id} className="border-b hover:bg-gray-50">
                          <td className="px-4 py-3 font-mono text-sm text-slate-700">{order.order_number}</td>
                          <td className="px-4 py-3 font-semibold text-slate-900">{order.item_name}</td>
                          <td className="px-4 py-3 text-slate-700 font-medium">{order.quantity}</td>
                          <td className="px-4 py-3">
                            <Badge className={
                              order.status === 'delivered' ? 'bg-green-500' :
                              order.status === 'shipped' ? 'bg-blue-500' :
                              order.status === 'processing' ? 'bg-purple-500' :
                              'bg-orange-500'
                            }>{order.status}</Badge>
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-600">{new Date(order.expected_delivery).toLocaleDateString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="inventory" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-slate-900 font-bold">Inventory Status</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="px-4 py-3 text-sm font-bold text-slate-900">Item</th>
                        <th className="px-4 py-3 text-sm font-bold text-slate-900">Category</th>
                        <th className="px-4 py-3 text-sm font-bold text-slate-900">Stock</th>
                        <th className="px-4 py-3 text-sm font-bold text-slate-900">Min Level</th>
                        <th className="px-4 py-3 text-sm font-bold text-slate-900">Reorder Qty</th>
                        <th className="px-4 py-3 text-sm font-bold text-slate-900">Status</th>
                        <th className="px-4 py-3 text-sm font-bold text-slate-900">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dashboardData?.inventory?.map((item) => (
                        <tr key={item.id} className={`border-b hover:bg-gray-50 ${item.is_low ? 'bg-orange-50' : ''}`}>
                          <td className="px-4 py-3 font-semibold text-slate-900">{item.name}</td>
                          <td className="px-4 py-3"><Badge variant="outline">{item.category}</Badge></td>
                          <td className={`px-4 py-3 font-bold ${item.is_low ? 'text-red-600' : 'text-slate-900'}`}>{item.current_stock}</td>
                          <td className="px-4 py-3 text-slate-600">{item.min_stock}</td>
                          <td className="px-4 py-3 text-slate-600">{item.reorder_qty}</td>
                          <td className="px-4 py-3">
                            {item.is_low ? (
                              <Badge className="bg-red-500">Low Stock</Badge>
                            ) : (
                              <Badge className="bg-green-500">OK</Badge>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <Button size="sm" variant="outline" onClick={() => {
                              setSelectedItem(item);
                              setReorderQty(item.reorder_qty);
                              setShowReorderDialog(true);
                            }}>
                              <ShoppingCart className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="orders" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-slate-900 font-bold">All Orders</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="px-4 py-3 text-sm font-bold text-slate-900">Order #</th>
                        <th className="px-4 py-3 text-sm font-bold text-slate-900">Item</th>
                        <th className="px-4 py-3 text-sm font-bold text-slate-900">Category</th>
                        <th className="px-4 py-3 text-sm font-bold text-slate-900">Qty</th>
                        <th className="px-4 py-3 text-sm font-bold text-slate-900">Type</th>
                        <th className="px-4 py-3 text-sm font-bold text-slate-900">Status</th>
                        <th className="px-4 py-3 text-sm font-bold text-slate-900">Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dashboardData?.recent_orders?.map((order) => (
                        <tr key={order.id} className="border-b hover:bg-gray-50">
                          <td className="px-4 py-3 font-mono text-sm text-slate-700">{order.order_number}</td>
                          <td className="px-4 py-3 font-semibold text-slate-900">{order.item_name}</td>
                          <td className="px-4 py-3"><Badge variant="outline">{order.category}</Badge></td>
                          <td className="px-4 py-3 text-slate-900 font-bold">{order.quantity}</td>
                          <td className="px-4 py-3">
                            <Badge className={order.trigger_type === 'auto' ? 'bg-blue-500' : 'bg-purple-500'}>
                              {order.trigger_type}
                            </Badge>
                          </td>
                          <td className="px-4 py-3">
                            <Badge className={
                              order.status === 'delivered' ? 'bg-green-500' :
                              order.status === 'shipped' ? 'bg-blue-500' :
                              order.status === 'processing' ? 'bg-purple-500' :
                              'bg-orange-500'
                            }>{order.status}</Badge>
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-600">{new Date(order.created_at).toLocaleDateString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Reorder Dialog */}
      <Dialog open={showReorderDialog} onOpenChange={setShowReorderDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-slate-900 font-bold">Trigger Reorder</DialogTitle>
            <DialogDescription className="text-slate-600">Place a manual reorder for {selectedItem?.name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="font-semibold text-slate-900">Current Stock</Label>
              <p className="text-lg font-bold text-red-600">{selectedItem?.current_stock}</p>
            </div>
            <div>
              <Label className="font-semibold text-slate-900">Reorder Quantity</Label>
              <Input type="number" value={reorderQty} onChange={(e) => setReorderQty(parseInt(e.target.value))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReorderDialog(false)}>Cancel</Button>
            <Button className="bg-teal-600 hover:bg-teal-700" onClick={triggerReorder}>
              <ShoppingCart className="h-4 w-4 mr-2" />
              Place Order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AutoReorderDashboard;
